import type { ReactElement } from 'react';
import { Badge } from '@/components/ui/badge';
import { formatExtraKey } from './utils';

function ExtraValue({
  keyName,
  value,
}: {
  keyName: string;
  value: string;
}): ReactElement {
  const isBoolean = value === 'true' || value === 'false';
  const isList =
    value.includes(',') && !keyName.toLowerCase().includes('command');
  const isWarning =
    keyName.toLowerCase().includes('exposed') ||
    keyName.toLowerCase().includes('privileged') ||
    keyName.toLowerCase().includes('sensitive');

  if (isBoolean) {
    return (
      <Badge
        variant={
          value === 'true'
            ? isWarning
              ? 'destructive'
              : 'default'
            : 'secondary'
        }
        className="text-xs"
      >
        {value === 'true' ? 'Yes' : 'No'}
      </Badge>
    );
  }

  if (isList) {
    const items = value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    return (
      <div className="flex flex-wrap gap-1">
        {items.map((item, idx) => (
          <Badge key={idx} variant="outline" className="text-xs font-mono">
            {item}
          </Badge>
        ))}
      </div>
    );
  }

  if (isWarning && value) {
    return <span className="text-sm font-mono text-destructive">{value}</span>;
  }

  return <span className="text-sm font-mono">{value}</span>;
}

export function ExtrasCard({
  extras,
}: {
  extras: Record<string, string>;
}): ReactElement | null {
  if (!extras || Object.keys(extras).length === 0) return null;

  const nonEmptyEntries = Object.entries(extras).filter(
    ([, value]) => value !== '' && value !== null && value !== undefined
  );

  if (nonEmptyEntries.length === 0) return null;

  return (
    <div className="space-y-2">
      {nonEmptyEntries.map(([key, value]) => (
        <div key={key} className="flex items-start gap-2">
          <span className="text-muted-foreground text-sm whitespace-nowrap">
            {formatExtraKey(key)}:
          </span>
          <ExtraValue keyName={key} value={value} />
        </div>
      ))}
    </div>
  );
}
